/* eslint-disable sonarjs/cognitive-complexity */
import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, message, Modal, Input, Alert } from 'antd';
import styled from 'styled-components';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import moment from 'moment';
import { Box, Text } from 'atoms';
import { ACL, EditModeBtn } from 'components/common';
import { createClientContractV2, updateClientContractV2 } from 'store/clientSliceV2';
import { getSignedUrl } from 'store/commonSlice';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';
import ContractEditHistoryTable from 'componentsv2/contract/ContractEditHistoryTable';
import SimilarContracts from 'componentsv2/contract/SimilarContracts';
import { buildChangesDiff, CLIENT_CONTRACT_LOCKED_FIELDS } from './contractEditFlow.helpers';
import {
	BILLING_TYPE_FIXED,
	BILLING_TYPE_FIXED_VARIABLE,
	BILLING_TYPE_VARIABLE,
	BILLING_TYPE_FIXED_PER_DAY,
	FormSchema,
	getInitialValues,
} from './ClientContractFormV2.constants';
import {
	BasicInfoFields,
	FixedBillingFields,
	ChargesPerKmField,
	VariableCostFields,
	AdditionalInfoFields,
	FixedRatePerDayField,
} from './ClientContractFormV2.fields';
import PerOrderRatesField, {
	SUB_BILLING_PER_KM,
	SUB_BILLING_PER_ORDER,
} from './PerOrderRatesField';

/**
 * Extract filename from S3 key by removing UUID prefix
 * S3 keys are in format: {uuid}-{filename}
 * Example: "340d1b25-c372-439e-b92c-5306e4748a3-Data_Analytics_Assignment.pdf"
 * Returns: "Data_Analytics_Assignment.pdf"
 */
const extractFilenameFromS3Key = (s3Key) => {
	if (!s3Key) return '';
	// UUID format: 8-4-4-4-12 characters (36 chars total + 1 hyphen)
	// Find the position after the UUID prefix (first 37 characters)
	const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i;
	const match = s3Key.match(uuidPattern);
	if (match) {
		return s3Key.substring(match[0].length);
	}
	// If no UUID pattern found, return the original key
	return s3Key;
};

const PageContainer = styled(Box)`
	padding: 1rem 0;
`;

const FormTitle = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #1c1c1c;
	margin-bottom: 4px;
	display: block;
`;

const FormSubtitle = styled(Text)`
	font-size: 14px;
	color: #707070;
	margin-bottom: 1.5rem;
	display: block;
`;

const FormContainer = styled(Box)`
	background: #fff;
	border-radius: 4px;
	padding: 2rem;
	margin-bottom: 1.5rem;
`;

const ButtonContainer = styled(Box)`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;

const SubmitButton = styled(Button)`
	background-color: #18405d;
	color: #fff;
	border: none;
	border-radius: 4px;
	padding: 8px 24px;
	height: auto;
	font-size: 15px;
	font-weight: 500;

	&:hover {
		background-color: #153e60;
		color: #fff;
	}
`;

const CancelButton = styled(Button)`
	background: #fff;
	color: #595959;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	padding: 8px 24px;
	height: auto;
	font-size: 15px;
	font-weight: 500;

	&:hover {
		background: #f5f5f5;
		color: #595959;
		border-color: #d9d9d9;
	}
`;

export default function ClientContractFormV2({
	isEditMode = false,
	isViewMode = false,
	setIsViewMode = () => {},
	formData = {},
	clientsData = [],
	vehicleData = [],
	projectData = [],
	routeData = [],
	onFetchRoutes = () => {},
	onSearchClients = () => {},
	onSearchProjects = () => {},
	onSearchVehicleTypes = () => {},
	onVehicleTypeFetch = () => {},
	loadingClients = false,
	loadingProjects = false,
	loadingVehicleTypes = false,
	loadingRoutes = false,
}) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [contractDocumentKey, setContractDocumentKey] = useState(null);
	const [uploadingDocument, setUploadingDocument] = useState(false);
	const [uploadedFileDetails, setUploadedFileDetails] = useState(null);
	const [documentModalVisible, setDocumentModalVisible] = useState(false);

	const isEditFlow = isEditMode && Boolean(formData?.id);
	const [editFlowModalOpen, setEditFlowModalOpen] = useState(false);
	const [editFlowComments, setEditFlowComments] = useState('');
	const [editFlowPending, setEditFlowPending] = useState(null);
	const [editFlowSubmitting, setEditFlowSubmitting] = useState(false);

	const initialValues = useMemo(
		() => getInitialValues(formData, isEditMode),
		[formData, isEditMode]
	);
	const originalRateIds = useMemo(
		() => new Set((formData?.order_rates || []).map((r) => String(r.rate_id))),
		[formData]
	);

	// Initialize document states from formData when in edit/view mode
	useEffect(() => {
		if (isEditMode && formData?.document_info?.agreement_file?.document_key) {
			const documentKey = formData.document_info.agreement_file.document_key;
			setContractDocumentKey(documentKey);

			// Extract filename and set file details for display
			const filename = extractFilenameFromS3Key(documentKey);
			setUploadedFileDetails({
				name: filename,
				// Size is not available from API, so we'll show the file without size
				size: null,
				type: 'application/pdf', // Assume PDF for display purposes
			});
		}
	}, [isEditMode, formData]);

	const handleSubmit = async (values, { setErrors, setSubmitting }) => {
		try {
			const {
				id,
				client_id,
				project_id,
				billing_type,
				temperature_controlled,
				vehicle_type_id,
				capacity_tonnes,
				contract_date,
				expiry_date,
				monthly_off,
				route_id,
				// Fixed + Variable and Fixed fields
				fixed_hours_per_day,
				threshold_km_per_day,
				threshold_km_per_month,
				// Common cost fields
				vehicle_cost,
				loader_cost,
				delivery_associate_cost,
				// Fixed + Variable specific
				extra_charges_per_trip,
				extra_charges_per_hour,
				// Variable › Per Km specific
				charges_per_km,
				// Fixed Per Day specific
				fixed_rate_per_day,
				other_other_expenses,
				// Sub billing type + per-order rate lines
				billing_subtype,
				per_order_rates,
				// Other expenses: text field maps to other_expenses_type, number field maps to other_expenses_value
				other_expenses,
			} = values || {};

			// Convert temperature_controlled to boolean
			// If it's already a boolean, use it; if it's a string "Yes"/"No", convert it
			let temperatureControlledBool = false;
			if (typeof temperature_controlled === 'boolean') {
				temperatureControlledBool = temperature_controlled;
			} else if (typeof temperature_controlled === 'string') {
				temperatureControlledBool =
					temperature_controlled.toLowerCase() === 'yes' ||
					temperature_controlled === 'true';
			} else if (temperature_controlled === true || temperature_controlled === 1) {
				temperatureControlledBool = true;
			}

			// Convert monthly_off array to comma-separated string or first value
			// Backend expects a string, but frontend uses array for multi-select
			const monthlyOffValue = Array.isArray(monthly_off)
				? monthly_off.join(',')
				: monthly_off;

			// Build base payload (common fields for all billing types)
			const payload = {
				...(isEditMode && id ? { id } : {}),
				client_id,
				project_id,
				billing_type,
				temperature_controlled: temperatureControlledBool,
				route_id,
				vehicle_type_id,
				capacity_tonnes: Number(capacity_tonnes),
				contract_date: moment(contract_date).format('YYYY-MM-DD'),
				expiry_date: moment(expiry_date).format('YYYY-MM-DD'),
				monthly_off: monthlyOffValue,
				// Common cost fields (hidden / not applicable for Variable › Per Order)
				vehicle_cost: Number(vehicle_cost) || 0,
				loader_cost: Number(loader_cost) || 0,
				delivery_associate_cost: Number(delivery_associate_cost) || 0,
			};

			// Add billing type specific fields
			const isVariable = billing_type === BILLING_TYPE_VARIABLE;
			const isFixedVariable = billing_type === BILLING_TYPE_FIXED_VARIABLE;
			const isFixedPerDay = billing_type === BILLING_TYPE_FIXED_PER_DAY;
			const isPerKm = billing_subtype === SUB_BILLING_PER_KM;
			const isPerOrder = billing_subtype === SUB_BILLING_PER_ORDER;
			const toNum = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));
			if (isVariable || isFixedVariable || isFixedPerDay) {
				payload.billing_subtype = billing_subtype;
			}

			// Fixed-side cost fields: plain Fixed, any Fixed + Variable, Fixed Per Day, or Variable › Per Km
			if (
				billing_type === BILLING_TYPE_FIXED ||
				isFixedVariable ||
				isFixedPerDay ||
				(isVariable && isPerKm)
			) {
				payload.fixed_hours_per_day = toNum(fixed_hours_per_day);
				payload.threshold_km_per_day = toNum(threshold_km_per_day);
				payload.threshold_km_per_month = toNum(threshold_km_per_month);
			}

			// Extra charges (per km / per hour): Fixed + Variable, Fixed Per Day, or Variable › Per Km
			if (isFixedVariable || isFixedPerDay || (isVariable && isPerKm)) {
				payload.extra_charges_per_trip = toNum(extra_charges_per_trip);
				payload.extra_charges_per_hour = toNum(extra_charges_per_hour);
			}

			if (isVariable) {
				payload.charges_per_km = toNum(charges_per_km);
			}
			if (isFixedPerDay) {
				payload.fixed_rate_per_day = toNum(fixed_rate_per_day);
			}
			if ((isVariable || isFixedVariable || isFixedPerDay) && isPerOrder) {
				payload.order_rates = (per_order_rates || []).map((r) =>
					originalRateIds.has(String(r.rate_id))
						? { rate_id: r.rate_id, rate: toNum(r.rate) }
						: { rate: toNum(r.rate) }
				);
			}
			if (billing_type) {
				payload.other_expenses_type = other_expenses || '';
				payload.other_expenses_value = toNum(other_other_expenses);
			}

			// Add document_info if document is uploaded
			if (contractDocumentKey) {
				payload.document_info = {
					agreement_file: {
						document_key: contractDocumentKey,
					},
				};
			}

			console.log('Submitting client contract with payload:', payload);
			if (isEditFlow && id) {
				const changedKeys = Object.keys(
					buildChangesDiff(values, initialValues, CLIENT_CONTRACT_LOCKED_FIELDS)
				);
				const FORM_TO_PAYLOAD_KEY = {
					other_expenses: 'other_expenses_type',
					other_other_expenses: 'other_expenses_value',
					per_order_rates: 'order_rates',
				};
				const changes = {};
				changedKeys.forEach((key) => {
					const payloadKey = FORM_TO_PAYLOAD_KEY[key] || key;
					if (payloadKey in payload) changes[payloadKey] = payload[payloadKey];
				});
				if (contractDocumentKey !== formData?.document_info?.agreement_file?.document_key) {
					changes.document_info = {
						agreement_file: { document_key: contractDocumentKey },
					};
				}
				if (Object.keys(changes).length === 0) {
					message.info('No changes detected');
					setSubmitting(false);
					return;
				}
				setEditFlowPending({ id, changes, setErrors });
				setEditFlowModalOpen(true);
				setSubmitting(false);
				return;
			}

			// Make API call - use update if editing, create if new
			let response;
			if (isEditMode && id) {
				response = await dispatch(
					updateClientContractV2({
						req_data: payload,
						setErrors,
					})
				);
				console.log('Client contract update response:', response);
			} else {
				response = await dispatch(
					createClientContractV2({
						req_data: payload,
						setErrors,
					})
				);
				console.log('Client contract creation response:', response);
			}

			// Check for error response from API
			if (response?.error || response?.status === 'error') {
				const errorMessage =
					response?.error?.message ||
					response?.message ||
					(isEditMode
						? 'Failed to update client contract. Please try again.'
						: 'Failed to create client contract. Please try again.');
				message.error(errorMessage, 5);
				setSubmitting(false);
				return;
			}

			// Check for successful response
			if (response?.data || response?.status === 'success' || response?.success) {
				message.success(
					isEditMode
						? 'Client contract updated successfully.'
						: 'Client contract created successfully. Pending approval.'
				);
				navigate('/client-contracts');
			} else {
				// Fallback error message
				const errorMessage =
					response?.error?.message ||
					response?.message ||
					(isEditMode
						? 'Failed to update client contract. Please try again.'
						: 'Failed to create client contract. Please try again.');
				message.error(errorMessage, 5);
				setSubmitting(false);
			}
		} catch (error) {
			console.error(`Error ${isEditMode ? 'updating' : 'creating'} client contract:`, error);

			// Extract error message from various possible error structures
			let errorMessage = 'An error occurred. Please try again.';

			if (error?.response?.data?.error?.message) {
				errorMessage = error.response.data.error.message;
			} else if (error?.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error?.message) {
				errorMessage = error.message;
			} else if (typeof error === 'string') {
				errorMessage = error;
			}

			message.error(
				errorMessage ||
					`An error occurred while ${
						isEditMode ? 'updating' : 'creating'
					} the client contract. Please try again.`,
				5
			);
			setSubmitting(false);
		}
	};

	const handleEdit = () => {
		setIsViewMode(false);
	};

	const handleCancel = () => {
		setIsViewMode(true);
	};

	const handleEditFlowCancel = () => {
		if (editFlowSubmitting) return;
		setEditFlowModalOpen(false);
		setEditFlowComments('');
		setEditFlowPending(null);
	};

	const handleEditFlowConfirm = async () => {
		const trimmed = (editFlowComments || '').trim();
		if (!trimmed) {
			message.error('Comments are required');
			return;
		}
		if (!editFlowPending) return;

		setEditFlowSubmitting(true);
		try {
			const response = await dispatch(
				updateClientContractV2({
					req_data: { id: editFlowPending.id },
					edit_flow: true,
					comments: trimmed,
					changes: editFlowPending.changes,
				})
			);
			if (response?.status === 'success' || response?.data) {
				message.success(response?.message || 'Edit submitted for approval');
				setEditFlowModalOpen(false);
				setEditFlowComments('');
				setEditFlowPending(null);
				setTimeout(() => navigate('/client-contracts'), 800);
			} else {
				message.error(response?.message || 'Failed to submit edit. Please try again.');
			}
		} catch (error) {
			const rawMsg =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message;
			const errMsg =
				rawMsg && typeof rawMsg === 'object'
					? Object.values(rawMsg).join(', ')
					: rawMsg || 'Failed to submit edit. Please try again.';
			message.error(errMsg);
		} finally {
			setEditFlowSubmitting(false);
		}
	};

	// Handle contract document removal
	const handleRemoveDocument = () => {
		setContractDocumentKey(null);
		setUploadedFileDetails(null);
		message.info('Document removed');
		// Note: Error will be shown again if user tries to submit without uploading
	};

	// Handle viewing contract document
	const handleViewDocument = () => {
		if (!contractDocumentKey) {
			message.warning('Document not available');
			return;
		}
		setDocumentModalVisible(true);
	};

	// Handle document modal close
	const handleDocumentModalClose = () => {
		setDocumentModalVisible(false);
	};

	// Handle contract document upload
	const handleContractFileUpload = async (file) => {
		if (!file) {
			message.error('Please select a file');
			return;
		}

		// File validation - match docimgpdffileformates
		const allowedTypes = [
			'application/pdf',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			'image/png',
			'image/jpeg',
			'image/jpg',
		];
		const allowedExtensions = ['.pdf', '.doc', '.docx', '.png', '.jpeg', '.jpg'];
		const maxSizeMB = 8; // 8MB as per old form

		// Check file type and extension
		const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
		const isValidType =
			allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);

		if (!isValidType) {
			message.error('You can only upload PDF, DOC, DOCX, PNG, JPG, JPEG files!');
			return;
		}
		if (file.size / 1024 / 1024 > maxSizeMB) {
			message.error(`File must be smaller than ${maxSizeMB}MB!`);
			return;
		}

		setUploadingDocument(true);

		try {
			// Step 1: Get signed URL
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'client_contract',
						keys: [file.name],
					},
				})
			);

			const fileData = signedUrlResponse?.data?.[0];
			if (!fileData?.url || !fileData?.filename) {
				throw new Error('Failed to get signed URL');
			}

			// Step 2: Upload file to S3
			await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			});

			// Step 3: Store document key and file details
			setContractDocumentKey(fileData.filename);
			setUploadedFileDetails({
				name: file.name,
				size: file.size,
				type: file.type,
			});
			message.success('Contract document uploaded successfully');

			// Clear file upload error if it exists
			// Access Formik context through a ref or callback
			// We'll handle this in the form component
		} catch (error) {
			console.error('File upload error:', error);
			message.error('Failed to upload contract document');
		} finally {
			setUploadingDocument(false);
		}
	};

	return (
		<PageContainer>
			<FormTitle>Client Contract</FormTitle>
			<FormSubtitle>
				{isEditMode
					? 'View/Edit client contract details'
					: 'Create client contract for their requirements'}
			</FormSubtitle>

			{isEditMode && formData?.has_pending_edit && (
				<Alert
					type="warning"
					showIcon
					message="Pending Edit Approval"
					description="This contract is inactive while an edit is awaiting approval."
					style={{ marginBottom: 16 }}
				/>
			)}

			{isEditMode && isViewMode && !formData?.has_pending_edit && (
				<ACL action="update" module="client_contract">
					<EditModeBtn
						handleEdit={handleEdit}
						disabled={
							['rejected', 'pending'].includes(formData?.approval_status) ||
							formData?.is_active === false
						}
					/>
				</ACL>
			)}

			<Formik
				initialValues={initialValues}
				validationSchema={FormSchema}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({
					values,
					errors,
					touched,
					isSubmitting,
					submitForm,
					validateForm,
					setTouched,
					setErrors,
					resetForm,
				}) => {
					// Debug: Log form state on render
					if (Object.keys(errors).length > 0) {
						console.log('Form validation errors:', errors);
						console.log('Touched fields:', touched);
						console.log('Current form values:', values);
					}

					// Calculate file upload error to avoid nested ternary
					let fileUploadError = null;
					if (touched.contract_document && errors.contract_document) {
						fileUploadError = errors.contract_document;
					} else if (!contractDocumentKey && touched.contract_document) {
						fileUploadError = 'Agreement file is required';
					}

					return (
						<>
							<FormContainer>
								<Form>
									<SimilarContracts
										contractType="client"
										excludeId={formData?.id}
									/>
									{/* Basic Information Fields */}
									<BasicInfoFields
										clientsData={clientsData}
										projectData={projectData}
										vehicleData={vehicleData}
										onProjectChange={async (projectId) => {
											if (projectId && values.vehicle_type_id) {
												await onFetchRoutes(
													projectId,
													values.vehicle_type_id
												);
											}
										}}
										onVehicleTypeChange={async (vehicleTypeId) => {
											if (vehicleTypeId && values.project_id) {
												await onFetchRoutes(
													values.project_id,
													vehicleTypeId
												);
											}
										}}
										onSearchClients={onSearchClients}
										onSearchProjects={onSearchProjects}
										onSearchVehicleTypes={onSearchVehicleTypes}
										onVehicleTypeFetch={onVehicleTypeFetch}
										loadingClients={loadingClients}
										loadingProjects={loadingProjects}
										loadingVehicleTypes={loadingVehicleTypes}
										isViewMode={isViewMode}
										isEditFlow={isEditFlow}
									/>

									{values.billing_type === BILLING_TYPE_FIXED && (
										<FixedBillingFields
											isViewMode={isViewMode}
											showExtraCharges={false}
										/>
									)}
									{((values.billing_type === BILLING_TYPE_VARIABLE &&
										values.billing_subtype === SUB_BILLING_PER_KM) ||
										values.billing_type === BILLING_TYPE_FIXED_VARIABLE ||
										values.billing_type === BILLING_TYPE_FIXED_PER_DAY) && (
										<FixedBillingFields isViewMode={isViewMode} />
									)}

									{values.billing_type === BILLING_TYPE_FIXED_PER_DAY && (
										<FixedRatePerDayField isViewMode={isViewMode} />
									)}

									{/* Charges per km — Variable billing (both Per Km and Per Order) */}
									{values.billing_type === BILLING_TYPE_VARIABLE && (
										<ChargesPerKmField isViewMode={isViewMode} />
									)}
									{values.billing_type === BILLING_TYPE_VARIABLE &&
										values.billing_subtype === SUB_BILLING_PER_ORDER && (
											<VariableCostFields isViewMode={isViewMode} />
										)}
									{(values.billing_type === BILLING_TYPE_VARIABLE ||
										values.billing_type === BILLING_TYPE_FIXED_VARIABLE ||
										values.billing_type === BILLING_TYPE_FIXED_PER_DAY) &&
										values.billing_subtype === SUB_BILLING_PER_ORDER && (
											<PerOrderRatesField
												isViewMode={isViewMode}
												isEditMode={isEditMode}
											/>
										)}

									{/* Additional Information: Other Expenses, Route, File Upload */}
									<AdditionalInfoFields
										billingType={values.billing_type}
										routeData={routeData}
										loadingRoutes={loadingRoutes}
										contractDocumentKey={contractDocumentKey}
										uploadingDocument={uploadingDocument}
										uploadedFileDetails={uploadedFileDetails}
										onContractFileUpload={handleContractFileUpload}
										onRemoveDocument={handleRemoveDocument}
										onViewDocument={handleViewDocument}
										isViewMode={isViewMode}
										isEditFlow={isEditFlow}
										fileUploadError={fileUploadError}
									/>
								</Form>
							</FormContainer>

							<ButtonContainer>
								{isEditMode && !isViewMode && (
									<CancelButton
										type="button"
										disabled={isSubmitting}
										onClick={() => {
											resetForm({ values: initialValues });
											handleCancel();
										}}
									>
										Cancel
									</CancelButton>
								)}
								<SubmitButton
									type="submit"
									disabled={isSubmitting || isViewMode}
									loading={isSubmitting}
									onClick={async (e) => {
										e.preventDefault();
										// Validate the schema and check the document together so
										// every error (rates, document, etc.) surfaces at once
										// instead of gating one behind the other.
										const validationErrors = await validateForm();
										const documentMissing = !contractDocumentKey;

										if (
											Object.keys(validationErrors).length === 0 &&
											!documentMissing
										) {
											submitForm();
											return;
										}

										// Mark every field that has an error as touched so its
										// message renders immediately.
										const touchedFields = {};
										Object.keys(validationErrors).forEach((field) => {
											touchedFields[field] = true;
										});
										if (documentMissing) {
											touchedFields.contract_document = true;
										}
										setTouched(touchedFields);

										if (documentMissing) {
											setErrors({
												...validationErrors,
												contract_document: 'Agreement file is required',
											});
											message.error('Please upload the contract document');
										}
									}}
								>
									{isSubmitting ? 'Submitting...' : 'Submit'}
								</SubmitButton>
							</ButtonContainer>
						</>
					);
				}}
			</Formik>

			{/* Document Preview Modal */}
			<FilePreviewModal
				visible={documentModalVisible}
				fileKey={contractDocumentKey}
				fileName={uploadedFileDetails?.name || 'Contract Agreement'}
				entity="client_contract"
				onClose={handleDocumentModalClose}
			/>
			<Modal
				title="Submit Edit for Approval"
				open={editFlowModalOpen}
				onCancel={handleEditFlowCancel}
				onOk={handleEditFlowConfirm}
				okText="Submit"
				cancelText="Cancel"
				confirmLoading={editFlowSubmitting}
				maskClosable={!editFlowSubmitting}
				closable={!editFlowSubmitting}
			>
				<Text style={{ display: 'block', marginBottom: 8 }}>
					Comments <span style={{ color: '#ff4d4f' }}>*</span>
				</Text>
				<Input.TextArea
					rows={4}
					value={editFlowComments}
					onChange={(e) => setEditFlowComments(e.target.value)}
					placeholder="Enter reason for this edit"
					maxLength={500}
				/>
			</Modal>

			{/* Edit history (renders only when API returns history rows) */}
			{isEditMode && formData?.id && (
				<ContractEditHistoryTable
					contractId={formData?.contract_id || formData?.id}
					contractType="client"
				/>
			)}
		</PageContainer>
	);
}

ClientContractFormV2.propTypes = {
	isEditMode: PropTypes.bool,
	isViewMode: PropTypes.bool,
	setIsViewMode: PropTypes.func,
	formData: PropTypes.object,
	clientsData: PropTypes.array,
	vehicleData: PropTypes.array,
	projectData: PropTypes.array,
	routeData: PropTypes.array,
	onFetchRoutes: PropTypes.func,
	onSearchClients: PropTypes.func,
	onSearchProjects: PropTypes.func,
	onSearchVehicleTypes: PropTypes.func,
	onVehicleTypeFetch: PropTypes.func,
	loadingClients: PropTypes.bool,
	loadingProjects: PropTypes.bool,
	loadingVehicleTypes: PropTypes.bool,
	loadingRoutes: PropTypes.bool,
};
